// ═══════════════════════════════════════════════════════════════
// processChordLyrics
// Post-traitement SVG : transforme les [Accord] dans les lignes W:
// en éléments positionnés en absolu au-dessus du texte de paroles.
//
// Stratégie : pour chaque ligne W: contenant des [Accord],
//  1. On réécrit le tspan avec le texte nettoyé (sans [Accord])
//  2. On mesure la position X de chaque accord via getComputedTextLength()
//  3. On injecte un <tspan x=... y=...> absolu pour chaque accord
//
// À appeler après chaque renderAbc() :
//   processChordLyrics(document.querySelector('#notation'));
// ═══════════════════════════════════════════════════════════════

const CHORD_REGEX = /\[([^\]]+)\]/g;

// Taille de l'accord relative à la taille de la ligne W:
const CHORD_FONT_SCALE = 0.78;
// Couleur de l'accord
const CHORD_COLOR = '#1a6bbf';
// Hauteur réservée au-dessus d'une ligne W: ayant des accords (× font-size)
const CHORD_MARGIN_RATIO = 0.95;

/**
 * @param {Element} container - L'élément DOM contenant le ou les SVG abcjs.
 */
export function processChordLyrics(container) {
    const svgEl = container.querySelector('svg');
    if (!svgEl) return;

    const groups = container.querySelectorAll('g[data-name="unalignedWords"]');
    if (!groups.length) return;

    groups.forEach(group => {
        const textEls = Array.from(group.querySelectorAll('text[data-name="unalignedWords"]'));
        if (!textEls.length) return;

        let cumulativeShift = 0;

        textEls.forEach(textEl => {
            const tspan = textEl.querySelector('tspan');
            if (!tspan) return;

            const raw = tspan.textContent;
            const currentY = parseFloat(textEl.getAttribute('y') || '0');

            // Applique le décalage cumulé des lignes précédentes
            if (cumulativeShift !== 0) {
                textEl.setAttribute('y', String(currentY + cumulativeShift));
            }

            CHORD_REGEX.lastIndex = 0;
            if (!CHORD_REGEX.test(raw)) return;

            const fs     = parseFloat(textEl.getAttribute('font-size') || '16');
            const margin = fs * CHORD_MARGIN_RATIO;
            const lineY  = parseFloat(textEl.getAttribute('y') || '0') + margin;

            textEl.setAttribute('y', String(lineY));
            cumulativeShift += margin;

            // ── 1. Nettoie le texte : retire tous les [Accord] ─────────
            CHORD_REGEX.lastIndex = 0;
            const cleanText = raw.replace(CHORD_REGEX, '');
            tspan.textContent = cleanText;

            // ── 2. Collecte les accords et leur position dans le texte nettoyé
            const chords = _parseChords(raw);

            // ── 3. Mesure les positions X et injecte les accords ────────
            _injectChordLabels(svgEl, textEl, tspan, chords, fs, lineY);
        });
    });
}

/**
 * Parse la ligne brute et retourne la liste des accords
 * avec leur position dans le texte nettoyé (sans les crochets).
 *
 * Ex: "[Am]Savoir [Em]sourire" →
 *   [ { name: 'Am', textBefore: '' },
 *     { name: 'Em', textBefore: 'Savoir ' } ]
 *
 * @param {string} raw
 * @returns {{ name: string, textBefore: string }[]}
 */
function _parseChords(raw) {
    const chords = [];
    let cleanCursor = 0; // position dans le texte nettoyé
    let lastRawIndex = 0;
    let match;

    CHORD_REGEX.lastIndex = 0;
    while ((match = CHORD_REGEX.exec(raw)) !== null) {
        // Texte brut entre la fin du dernier accord et ce nouvel accord
        const rawBefore = raw.slice(lastRawIndex, match.index);
        chords.push({
            name: match[1],
            // Texte nettoyé depuis le début de la ligne jusqu'à cet accord
            textBefore: raw.slice(0, match.index).replace(/\[[^\]]+\]/g, ''),
        });
        lastRawIndex = match.index + match[0].length;
    }
    return chords;
}

/**
 * Mesure la largeur du texte "before" et injecte un <tspan x y>
 * pour chaque accord dans le <g> parent du <text>.
 *
 * @param {SVGElement}  svgEl   - le <svg> racine (pour créer des éléments temporaires)
 * @param {SVGElement}  textEl  - le <text data-name="unalignedWords">
 * @param {SVGElement}  tspan   - le <tspan> contenant le texte nettoyé
 * @param {{ name: string, textBefore: string }[]} chords
 * @param {number}      fs      - font-size de la ligne
 * @param {number}      lineY   - y absolu de la ligne (baseline du texte)
 */
function _injectChordLabels(svgEl, textEl, tspan, chords, fs, lineY) {
    const SVG_NS   = 'http://www.w3.org/2000/svg';
    const chordFs  = Math.round(fs * CHORD_FONT_SCALE * 10) / 10;
    const originX  = parseFloat(tspan.getAttribute('x') || textEl.getAttribute('x') || '0');

    // Y de l'accord = lineY remontée de toute la marge réservée,
    // plus un petit ajustement visuel pour aligner sur la cap-height
    const chordY = lineY - fs * CHORD_MARGIN_RATIO + chordFs * 0.2;

    // Élément temporaire pour mesurer les largeurs de texte
    // On reprend exactement les attributs de police du <text> parent
    const probe = document.createElementNS(SVG_NS, 'text');
    probe.setAttribute('font-size',   String(fs));
    probe.setAttribute('font-family', textEl.getAttribute('font-family') || 'Times New Roman');
    probe.setAttribute('font-style',  textEl.getAttribute('font-style')  || 'normal');
    probe.setAttribute('font-weight', textEl.getAttribute('font-weight') || 'normal');
    probe.setAttribute('visibility',  'hidden');
    probe.setAttribute('x', '0');
    probe.setAttribute('y', '0');
    svgEl.appendChild(probe);

    chords.forEach(({ name, textBefore }) => {
        // Mesure la largeur du texte avant l'accord
        probe.textContent = textBefore;
        const textWidth = probe.getComputedTextLength();
        const chordX = originX + textWidth;

        // Crée le tspan accord en position absolue
        const chordTs = document.createElementNS(SVG_NS, 'tspan');
        chordTs.setAttribute('x',           String(chordX));
        chordTs.setAttribute('y',           String(chordY));
        chordTs.setAttribute('font-size',   String(chordFs));
        chordTs.setAttribute('font-weight', 'bold');
        chordTs.setAttribute('fill',        CHORD_COLOR);
        chordTs.textContent = name;

        // Insère dans le même <text> (après le tspan principal)
        textEl.appendChild(chordTs);
    });

    svgEl.removeChild(probe);
}